import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'plot');
  const [articles, setArticles] = useState<Article[]>([]);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const results = getArticlesByCategory(activeSlug);
    setArticles(results);
  }, [activeSlug]);

  const handleCategoryChange = (categorySlug: string) => {
    if (categorySlug === activeSlug) return;
    setIsChanging(true);
    setActiveSlug(categorySlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.history.pushState(null, '', `/category/${categorySlug}`);
    setTimeout(() => {
      setArticles(getArticlesByCategory(categorySlug));
      setIsChanging(false);
    }, 100);
  };

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: activeCategory?.name || '分类' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-4 mb-2">
              {activeCategory?.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              {activeCategory?.description}
            </p>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
          {isChanging ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-base text-foreground-500">该分类暂无文章</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-4 text-sm text-primary-600 hover:text-primary-700 no-underline"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `赤裸特工 - ${activeCategory?.name}`,
            description: activeCategory?.description,
            url: `https://www.chiluotegong.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '赤裸特工官方',
              url: 'https://www.chiluotegong.com/',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.chiluotegong.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: activeCategory?.name,
                item: `https://www.chiluotegong.com/category/${activeSlug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}